// translates settingsPage()'s plan cards (div#s7-plans.s7-plans > section.s7-plan, section.s7-plan-confirm; approved-workspace.html:1206) on the server's INR prices.
import * as React from 'react'
import { ProtoButton, ProtoError, ProtoLoading } from '@/features/d0/states'
import api from '@/services/api'
import { errorText } from '@/features/u2/shared'
import { payForPlan, type CheckoutOutcome } from './checkout'
import {
  APPROX_CURRENCIES,
  CYCLES,
  CYCLE_LABELS,
  CYCLE_PERIOD,
  TIERS,
  TIER_FEATURES,
  TIER_NAMES,
  approxDisplay,
  loadPricing,
  priceDisplay,
  type ApproxCurrency,
  type BillingCycle,
  type Pricing,
  type TierKey,
} from './pricing'

export interface PlanPickerProps {
  /** Replaces the Choose button for a tier (current plan, scheduled downgrade…); undefined keeps Choose. */
  renderAction?: (tier: TierKey) => React.ReactNode | undefined
  /** Marks that card `.current` ("YOUR PLAN"); null/undefined while on a trial or without a plan. */
  currentTier?: string | null
  onPaid: (outcome: CheckoutOutcome, tier: TierKey) => void | Promise<void>
}

/** Plan cards with the server's INR prices, coupon and Pay. Checkout.js loads on Pay only. */
export function PlanPicker({ renderAction, currentTier, onPaid }: PlanPickerProps) {
  const [pricing, setPricing] = React.useState<Pricing | null>(null)
  const [loadError, setLoadError] = React.useState<string | null>(null)
  const [cycle, setCycle] = React.useState<BillingCycle>('monthly')
  const [currency, setCurrency] = React.useState<'INR' | ApproxCurrency>('INR')
  const [selected, setSelected] = React.useState<TierKey | null>(null)
  const [coupon, setCoupon] = React.useState('')
  const [couponStatus, setCouponStatus] = React.useState<{ valid: boolean; message: string } | null>(null)
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [notice, setNotice] = React.useState<string | null>(null)

  const load = React.useCallback(() => {
    setLoadError(null)
    setPricing(null)
    loadPricing().then(setPricing, (e: unknown) => setLoadError(errorText(e)))
  }, [])
  React.useEffect(load, [load])

  const checkCoupon = async () => {
    setCouponStatus(null)
    try {
      setCouponStatus(await api.validateCoupon(coupon.trim()))
    } catch (e) {
      setCouponStatus({ valid: false, message: errorText(e) })
    }
  }

  const pay = async () => {
    if (!selected) return
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      const outcome = await payForPlan(selected, cycle, coupon.trim() || undefined)
      if (outcome.kind === 'dismissed') setNotice('Payment window closed. You have not been charged.')
      else await onPaid(outcome, selected)
    } catch (e) {
      setError(errorText(e))
    } finally {
      setBusy(false)
    }
  }

  if (loadError) return <ProtoError title="Prices could not be loaded" body={loadError} level={3} actions={<ProtoButton onClick={load}>Retry</ProtoButton>} />
  if (!pricing) return <ProtoLoading label="Loading prices…" />

  return (
    <>
      <div className="s7-plan-note">Prices are in Indian rupees and charged through Razorpay. Other currencies are shown as an approximation only.</div>
      <div className="s7-field-grid">
        <label className="z-field">Billing cycle
          <select value={cycle} onChange={(e) => setCycle(e.target.value as BillingCycle)}>
            {CYCLES.map((c) => <option key={c} value={c}>{CYCLE_LABELS[c]}</option>)}
          </select>
        </label>
        <label className="z-field">Show prices in
          <select value={currency} onChange={(e) => setCurrency(e.target.value as 'INR' | ApproxCurrency)}>
            <option value="INR">INR (charged)</option>
            {APPROX_CURRENCIES.map((c) => <option key={c} value={c}>{c} (approximate)</option>)}
          </select>
        </label>
      </div>
      <div id="s7-plans" className="s7-plans">
        {TIERS.map((tier) => {
          const custom = renderAction?.(tier)
          const current = tier === currentTier
          return (
            <section key={tier} className={current ? 's7-plan current' : 's7-plan'}>
              <span>{current ? 'YOUR PLAN' : tier === 'basic_plus' ? 'MORE ROOM TO CREATE' : 'WORKSPACE PLAN'}</span>
              <h3>{TIER_NAMES[tier]}</h3>
              <p><b>{priceDisplay(pricing, tier, cycle)}</b> / {CYCLE_PERIOD[cycle]}</p>
              {currency !== 'INR' ? <p>{approxDisplay(currency, tier, cycle)}</p> : null}
              <ul>
                {TIER_FEATURES[tier].map((f) => <li key={f}>{f}</li>)}
              </ul>
              {custom !== undefined ? custom : (
                <ProtoButton className="z-primary" aria-pressed={selected === tier} onClick={() => setSelected(tier)}>
                  {selected === tier ? `${TIER_NAMES[tier]} selected` : `Choose ${TIER_NAMES[tier]}`}
                </ProtoButton>
              )}
            </section>
          )
        })}
      </div>
      {selected ? (
        <section className="s7-plan-confirm">
          <h3>Change to {TIER_NAMES[selected]}?</h3>
          <p>{priceDisplay(pricing, selected, cycle)} / {CYCLE_PERIOD[cycle]} · billed in INR through Razorpay. Usage already recorded is kept.</p>
          <div className="s7-field-grid">
            <label className="z-field">Coupon code (optional)
              <input value={coupon} onChange={(e) => { setCoupon(e.target.value); setCouponStatus(null) }} />
            </label>
          </div>
          <ProtoButton className="z-quiet" disabled={!coupon.trim()} onClick={checkCoupon}>Check coupon</ProtoButton>
          {couponStatus ? <p role="status">{couponStatus.message}</p> : null}
          <div>
            <ProtoButton disabled={busy} onClick={() => setSelected(null)}>Keep current plan</ProtoButton>
            <ProtoButton className="z-primary" onClick={pay} disabled={busy || (couponStatus !== null && !couponStatus.valid)}>
              {busy ? 'Opening payment…' : `Pay ${priceDisplay(pricing, selected, cycle)} for ${TIER_NAMES[selected]}`}
            </ProtoButton>
          </div>
        </section>
      ) : null}
      {notice ? <p role="status" className="s7-meta">{notice}</p> : null}
      {error ? <p role="alert" className="z-error">{error}</p> : null}
    </>
  )
}
